import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'react-toastify';
import type { WhatsAppAccessMode, WhatsAppPhoneWhitelistEntry } from '@workspace/types';
import { whatsappAdminApi } from '../api/whatsappApi';

const DEFAULT_REJECTION_MESSAGE_PLACEHOLDER =
  'Hola. Este número de WhatsApp es de uso restringido y tu número no está habilitado para interactuar con él. Si pensás que es un error, contactá al administrador del canal.';

type WhatsAppPhoneWhitelistSectionProps = {
  channelId: string;
  accessMode: WhatsAppAccessMode;
  botLabel?: string;
  whitelistRejectionEnabled?: boolean;
  whitelistRejectionMessage?: string;
  onRejectionChange?: (enabled: boolean, message: string) => void;
  showRejectionSettings?: boolean;
  showSaveReminder?: boolean;
  compact?: boolean;
};

export function WhatsAppPhoneWhitelistSection({
  channelId,
  accessMode,
  botLabel,
  whitelistRejectionEnabled = false,
  whitelistRejectionMessage = '',
  onRejectionChange,
  showRejectionSettings = false,
  showSaveReminder = false,
  compact = false,
}: WhatsAppPhoneWhitelistSectionProps) {
  const [whitelist, setWhitelist] = useState<WhatsAppPhoneWhitelistEntry[]>([]);
  const [loadingPhones, setLoadingPhones] = useState(true);
  const [newPhone, setNewPhone] = useState('');
  const [newPhoneEmail, setNewPhoneEmail] = useState('');
  const [newPhoneLabel, setNewPhoneLabel] = useState('');

  const loadPhones = useCallback(async () => {
    try {
      setLoadingPhones(true);
      const phones = await whatsappAdminApi.listWhitelist(channelId);
      setWhitelist(phones);
    } catch (error) {
      toast.error((error as Error).message || 'Error al cargar teléfonos');
    } finally {
      setLoadingPhones(false);
    }
  }, [channelId]);

  useEffect(() => {
    void loadPhones();
  }, [loadPhones]);

  const handleAddPhone = async () => {
    if (!newPhone.trim()) {
      return;
    }
    const email = newPhoneEmail.trim();
    const label = newPhoneLabel.trim();
    try {
      await whatsappAdminApi.addPhone(
        channelId,
        newPhone.trim(),
        email || undefined,
        label || undefined
      );
      setNewPhone('');
      setNewPhoneEmail('');
      setNewPhoneLabel('');
      await loadPhones();
      toast.success('Teléfono agregado');
    } catch (error) {
      toast.error((error as Error).message || 'Error al agregar teléfono');
    }
  };

  const handleRemovePhone = async (phone: string) => {
    try {
      await whatsappAdminApi.removePhone(channelId, phone);
      setWhitelist(current => current.filter(entry => entry.phoneE164 !== phone));
      toast.success('Teléfono eliminado');
    } catch (error) {
      toast.error((error as Error).message || 'Error al eliminar teléfono');
    }
  };

  const isRestricted = accessMode === 'WHITELIST_PHONES';

  const content = (
    <div className='space-y-4'>
      {!isRestricted && (
        <p className='text-sm rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2 text-amber-900 dark:text-amber-100'>
          El canal está en modo <strong>Abierto</strong>: cualquier número puede escribir. Para
          aplicar esta lista, cambiá el modo de acceso a <strong>Whitelist de teléfonos</strong> en{' '}
          <Link to='/admin/whatsapp' className='underline font-medium'>
            Administración → WhatsApp
          </Link>{' '}
          y guardá los cambios.
        </p>
      )}

      {showRejectionSettings && isRestricted && onRejectionChange && (
        <div className='rounded-lg border p-3 space-y-3'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex-1'>
              <Label className='font-medium'>Notificar a números no autorizados</Label>
              <p className='text-xs text-muted-foreground mt-1'>
                Si está activo, a quien escriba sin estar en la whitelist se le responde una vez cada
                24h con el mensaje de abajo.
              </p>
            </div>
            <Switch
              checked={whitelistRejectionEnabled}
              onCheckedChange={checked =>
                onRejectionChange(checked, whitelistRejectionMessage)
              }
            />
          </div>
          {whitelistRejectionEnabled && (
            <div>
              <Label className='text-xs'>Mensaje de rechazo (máx. 1024 caracteres)</Label>
              <Textarea
                value={whitelistRejectionMessage}
                onChange={e =>
                  onRejectionChange(whitelistRejectionEnabled, e.target.value.slice(0, 1024))
                }
                placeholder={DEFAULT_REJECTION_MESSAGE_PLACEHOLDER}
                rows={3}
              />
              <p className='text-xs text-muted-foreground mt-1'>
                {whitelistRejectionMessage.length}/1024 · Dejá vacío para usar el mensaje por defecto.
              </p>
            </div>
          )}
          {showSaveReminder && (
            <p className='text-xs text-muted-foreground'>
              Guardá los cambios del canal para aplicar la configuración de rechazo.
            </p>
          )}
        </div>
      )}

      <div className='grid gap-2 md:grid-cols-3'>
        <div>
          <Label className='text-xs'>Teléfono E.164 sin +</Label>
          <Input
            value={newPhone}
            onChange={e => setNewPhone(e.target.value)}
            placeholder='5491112345678'
          />
        </div>
        <div>
          <Label className='text-xs'>Email (opcional)</Label>
          <Input
            type='email'
            value={newPhoneEmail}
            onChange={e => setNewPhoneEmail(e.target.value)}
            placeholder='juan@empresa.com'
          />
        </div>
        <div>
          <Label className='text-xs'>Etiqueta (opcional)</Label>
          <Input
            value={newPhoneLabel}
            onChange={e => setNewPhoneLabel(e.target.value)}
            placeholder='Juan – Marketing'
          />
        </div>
      </div>
      <div className='flex justify-end'>
        <Button type='button' onClick={() => void handleAddPhone()} disabled={!newPhone.trim()}>
          Agregar teléfono
        </Button>
      </div>

      {loadingPhones ? (
        <p className='text-sm text-muted-foreground'>Cargando teléfonos…</p>
      ) : whitelist.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          {isRestricted
            ? 'Aún no hay números cargados. Mientras esté en modo Whitelist, el bot rechaza todos los mensajes entrantes.'
            : 'No hay números cargados. Podés agregarlos ahora; se aplicarán al activar Whitelist de teléfonos.'}
        </p>
      ) : (
        <div className='space-y-2'>
          {whitelist.map(entry => (
            <div
              key={entry.id}
              className='flex items-center justify-between border rounded-lg p-2 gap-2'
            >
              <div className='flex-1 min-w-0'>
                <div className='font-mono text-sm'>{entry.phoneE164}</div>
                <div className='text-xs text-muted-foreground truncate'>
                  {entry.email ?? 'sin email'}
                  {entry.label ? ` · ${entry.label}` : ''}
                </div>
              </div>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                onClick={() => void handleRemovePhone(entry.phoneE164)}
                title='Eliminar'
              >
                <Trash2 className='h-4 w-4 text-red-500' />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (compact) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Whitelist de teléfonos{botLabel ? ` — ${botLabel}` : ''}</CardTitle>
        <CardDescription>
          Números autorizados para escribir por WhatsApp a este bot. Formato E.164 sin signo + (ej.
          5491112345678).
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
